import React, { useState, useRef, useEffect, Fragment, useMemo } from 'react';
import { 
  Save, ChevronDown, ChevronRight, ShieldCheck, Search, RotateCcw, 
  Info, Loader2, Check, Minus 
} from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';


/**
 * IndeterminateCheckbox component handles three states: checked, unchecked, and indeterminate (partially checked).
 */
function IndeterminateCheckbox({
  checked,
  indeterminate,
  onChange,
  disabled,
  size = "md",
  title = ""
}) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate ?? false;
    }
  }, [indeterminate]);

  const sizeClass = size === "sm" ? "w-4 h-4" : "w-5 h-5";

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      title={title}
      className={`${sizeClass} rounded border-gray-300 dark:border-gray-600 text-primary cursor-pointer accent-primary disabled:opacity-40 disabled:cursor-not-allowed transition-all focus:ring-primary focus:ring-offset-0`}
    />
  );
}

/**
 * PermissionCell renders an individual checkbox for a role-permission assignment.
 */
function PermissionCell({
  checked,
  onChange,
  autoEnabled,
  disabled,
  title
}) {
  return (
    <td className="text-center py-2.5 px-2 relative group">
      <div className="flex items-center justify-center">
        <IndeterminateCheckbox
          checked={checked}
          onChange={onChange}
          disabled={disabled || autoEnabled}
          title={title}
        />
        {autoEnabled && checked && (
          <span 
            className="absolute top-1 right-1 w-2 h-2 rounded-full bg-primary/60" 
            title="Auto-enabled by dependency" 
          />
        )}
      </div>
    </td>
  );
}

function PermissionInfoPopover({ perm, allPermissions, getPermissionDescription }) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setOpen(false);
    }, 150);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button 
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none"
        >
          <Info className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent 
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="w-80 p-4 border border-gray-200 dark:border-gray-700 shadow-lg bg-white dark:bg-gray-800 rounded-xl pointer-events-auto" 
        side="right"
        sideOffset={8}
        align="start"
      >
        <div className="space-y-3">
          <div>
            <p className="text-xs text-gray-700 dark:text-gray-300 font-medium leading-relaxed">
              {getPermissionDescription(perm)}
            </p>
          </div>
          {perm.dependsOn && perm.dependsOn.length > 0 && (
            <div className="border-t border-gray-100 dark:border-gray-800 pt-2">
              <span className="text-[10px] font-semibold text-gray-400 block mb-1">Requires Permission(s)</span>
              <div className="flex flex-wrap gap-1">
                {perm.dependsOn.map(d => {
                  const dep = allPermissions.find(ap => ap.id === d);
                  return (
                    <span key={d} className="text-[9px] bg-primary/10 text-primary font-semibold px-1.5 py-0.5 rounded">
                      {dep?.name || d}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          {(perm.isSystemOnly || perm.isDangerous || perm.isTenantScoped !== undefined) && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {perm.isSystemOnly && (
                <span className="text-[9px] bg-amber-500/10 text-amber-500 font-semibold px-1.5 py-0.5 rounded border border-amber-500/20">
                  System Only
                </span>
              )}
              {perm.isDangerous && (
                <span className="text-[9px] bg-rose-500/10 text-rose-500 font-semibold px-1.5 py-0.5 rounded border border-rose-500/20">
                  Dangerous
                </span>
              )}
              {perm.isTenantScoped && (
                <span className="text-[9px] bg-blue-500/10 text-blue-500 font-semibold px-1.5 py-0.5 rounded border border-blue-500/20">
                  Tenant Scoped
                </span>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function RolesPermissionsMatrix({
  initialRoles,
  allPermissions,
  onSave,
  isSaving,
  currentUser,
  canAdminEditRole,
  isPermissionInScope
}) {
  // --- Data Transformation Helpers ---
  
  // Convert roles from API format to matrix state: { roleId: { permId: boolean } }
  const buildInitialState = () => {
    const state = {};
    initialRoles.forEach(role => {
      state[role.id] = {};
      role.permissions.forEach(permId => {
        state[role.id][permId] = true;
      });
    });
    return state;
  };

  const [permissionsState, setPermissionsState] = useState(buildInitialState);
  const [savedState, setSavedState] = useState(buildInitialState);

  // Sync internal states when initialRoles changes (e.g. after a silent refresh)
  useEffect(() => {
    const newState = buildInitialState();
    setPermissionsState(newState);
    setSavedState(newState);
  }, [initialRoles]);
  const [expandedModules, setExpandedModules] = useState(new Set());
  const [search, setSearch] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Group permissions by category
  const permissionModules = useMemo(() => {
    const groups = allPermissions.reduce((acc, perm) => {
      const category = perm.category || "Uncategorized";
      if (!acc[category]) acc[category] = { key: category, label: category, permissions: [] };
      acc[category].permissions.push({
        key: perm.id,
        label: perm.name,
        description: perm.description,
        resource: perm.resource,
        action: perm.action,
        isDangerous: perm.is_dangerous,
        isSystemOnly: perm.is_system_only,
        isTenantScoped: perm.is_tenant_scoped,
        dependsOn: perm.depends_on || [] 
      });
      return acc;
    }, {});
    
    return Object.values(groups).sort((a, b) => a.label.localeCompare(b.label));
  }, [allPermissions]);

  const hasChanges = JSON.stringify(permissionsState) !== JSON.stringify(savedState);

  // --- Logic Handlers ---

  const toggleModule = (moduleKey) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(moduleKey)) next.delete(moduleKey);
      else next.add(moduleKey);
      return next;
    });
  };

  const handlePermissionChange = (roleId, permId, value) => {
    setPermissionsState((prev) => {
      const rolePerms = { ...prev[roleId], [permId]: value };
      
      const perm = allPermissions.find(p => p.id === permId);
      const permName = perm?.name || "";

      // ── Granular Logic: "Edit implies View" (and any action implies View/Read) ──
      if (value) {
        // If enabling any action that isn't 'view' or 'read', auto-enable the module's 'view' or 'read' permission
        if (permName.includes(':') && !permName.endsWith(':view') && !permName.endsWith(':read')) {
          const [module] = permName.split(':');
          const viewPerm = allPermissions.find(p => p.name === `${module}:view` || p.name === `${module}:read`);
          if (viewPerm) {
            rolePerms[viewPerm.id] = true;
          }
        }

        // Handle explicit backend-defined dependencies
        if (perm?.depends_on) {
          perm.depends_on.forEach(depId => {
            rolePerms[depId] = true;
          });
        }
      } else {
        // If disabling 'view' or 'read', automatically disable EVERYTHING else in that module
        if (permName.endsWith(':view') || permName.endsWith(':read')) {
          const [module] = permName.split(':');
          allPermissions.forEach(p => {
            if (p.name.startsWith(`${module}:`) && p.id !== permId) {
              rolePerms[p.id] = false;
            }
          });
        }

        // Handle disabling things that depend on this permission
        allPermissions.forEach(p => {
          if (p.depends_on?.includes(permId)) {
            rolePerms[p.id] = false;
          }
        });
      }

      return { ...prev, [roleId]: rolePerms };
    });
  };

  const handleModuleRoleToggle = (roleId, moduleKey, value) => {
    const mod = permissionModules.find(m => m.key === moduleKey);
    if (!mod) return;

    setPermissionsState((prev) => {
      const rolePerms = { ...prev[roleId] };
      mod.permissions.forEach(p => {
        // Only toggle if in scope/editable (optional refinement)
        rolePerms[p.key] = value;
      });
      return { ...prev, [roleId]: rolePerms };
    });
  };

  const handleSave = async () => {
    // Convert back to API format: { roleId: [permIds] }
    const updates = {};
    Object.entries(permissionsState).forEach(([roleId, perms]) => {
      updates[roleId] = Object.entries(perms)
        .filter(([_, enabled]) => enabled)
        .map(([id, _]) => id);
    });

    const success = await onSave(updates);
    if (success) {
      setSavedState(permissionsState);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
    }
  };

  const handleReset = () => {
    setPermissionsState(savedState);
  };

  const getModuleState = (roleId, moduleKey) => {
    const mod = permissionModules.find(m => m.key === moduleKey);
    if (!mod) return "none";
    const roleState = permissionsState[roleId] || {};
    const enabledCount = mod.permissions.filter(p => roleState[p.key]).length;
    if (enabledCount === 0) return "none";
    if (enabledCount === mod.permissions.length) return "all";
    return "some";
  };

  const filteredModules = permissionModules.filter((mod) => {
    if (!search) return true;
    const lower = search.toLowerCase();
    return (
      mod.label.toLowerCase().includes(lower) ||
      mod.permissions.some(p => p.label.toLowerCase().includes(lower) || p.key.toLowerCase().includes(lower))
    );
  });

  const getPermissionDescription = (perm) => {
    if (perm.description && perm.description.trim() && perm.description !== "No description provided." && !perm.description.includes("Allows executing '") && !perm.description.includes("Allows performing the '")) {
      return perm.description;
    }
    
    const label = perm.label || "";
    const key = perm.key || "";
    
    if (key.includes(':')) {
      const [module, action] = key.split(':');
      const formattedModule = module.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      
      switch (action.toLowerCase()) {
        case 'view':
        case 'read':
          return `Allows viewing and reading ${formattedModule} settings, assets, and records.`;
        case 'create':
          return `Allows creating new ${formattedModule} records and resources.`;
        case 'write':
          return `Allows writing, composing, and adding entries for ${formattedModule}.`;
        case 'update':
        case 'edit':
          return `Allows editing, modifying, and updating existing configurations for ${formattedModule}.`;
        case 'delete':
          return `Allows permanently deleting or removing ${formattedModule} items from the platform.`;
        case 'manage':
        case 'admin':
          return `Allows full administrative control, configuration, and management of the ${formattedModule} module.`;
        case 'export':
          return `Allows exporting data, reports, or lists from ${formattedModule} to CSV or external formats.`;
        case 'execute':
        case 'run':
          return `Allows running, executing, and triggering processes or actions for ${formattedModule}.`;
        default:
          const formattedAction = action.charAt(0).toUpperCase() + action.slice(1);
          return `Allows performing the '${formattedAction}' operation on the ${formattedModule} module.`;
      }
    }
    
    return `Allows accessing the ${label} features.`;
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900 overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
      {/* Toolbar */}
      <div className="bg-gray-50/50 dark:bg-gray-800/30 border-b border-gray-200 dark:border-gray-800 px-6 py-2 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="search"
              placeholder="Search modules or permissions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
            />
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            {hasChanges && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 font-medium">
                <Info className="w-3.5 h-3.5" />
                Unsaved changes
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {hasChanges && (
            <button
              onClick={handleReset}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Discard
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all shadow-sm ${
              saveSuccess
                ? "bg-emerald-600 text-white"
                : hasChanges
                ? "bg-primary text-white hover:opacity-90"
                : "bg-gray-100 dark:bg-gray-800 text-gray-400 border border-gray-200 dark:border-gray-700 cursor-not-allowed"
            }`}
          >
            {isSaving ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : saveSuccess ? (
              <Check className="w-3.5 h-3.5" />
            ) : (
              <Save className="w-3.5 h-3.5" />
            )}
            {saveSuccess ? "Saved!" : isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Table Container */}
      <div className="flex-1 overflow-auto">
        <table className="min-w-max w-full border-collapse">
          <thead className="sticky top-0 z-20">
            <tr className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
              <th className="text-left py-3 px-6 text-[11px] font-black text-gray-900 dark:text-gray-100 uppercase tracking-widest min-w-[300px] sticky left-0 bg-gray-50 dark:bg-[#0f172a] z-30 shadow-[1px_0_0_0_#e2e8f0] dark:shadow-[1px_0_0_0_#334155]">
                Permission
              </th>
              {initialRoles.map((role, idx) => {
                // In the "All" view: draw a visual separator before the first PARTNER clone block
                const prevRole = initialRoles[idx - 1];
                const isFirstClone =
                  role.role_owner_type === 'PARTNER' &&
                  (!prevRole || prevRole.role_owner_type === 'PLATFORM');

                return (
                  <th
                    key={role.id}
                    className={`py-3 px-3 text-center min-w-[120px] ${
                      isFirstClone ? 'border-l-2 border-blue-300 dark:border-blue-700' : ''
                    }`}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-xs font-bold text-gray-800 dark:text-gray-200 leading-tight">
                        {role.name}
                      </span>
                      <span className="text-[9px] font-bold text-gray-400 dark:text-gray-500 bg-gray-100/80 dark:bg-gray-800/80 px-1.5 py-0.5 rounded uppercase tracking-tighter">
                        {role.scope}
                      </span>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {filteredModules.map((mod) => {
              const isExpanded = expandedModules.has(mod.key);
              const filteredPerms = search
                ? mod.permissions.filter(p => 
                    p.label.toLowerCase().includes(search.toLowerCase()) ||
                    p.key.toLowerCase().includes(search.toLowerCase()) ||
                    mod.label.toLowerCase().includes(search.toLowerCase())
                  )
                : mod.permissions;

              if (filteredPerms.length === 0) return null;

              return (
                <Fragment key={mod.key}>
                  {/* Module Header Row */}
                  <tr
                    className="bg-gray-100/50 dark:bg-gray-800/80 cursor-pointer select-none hover:bg-gray-200/50 dark:hover:bg-gray-700 transition-colors group border-y border-gray-200/50 dark:border-gray-700/50"
                    onClick={() => toggleModule(mod.key)}
                  >
                    <td className="py-2.5 px-6 sticky left-0 bg-gray-100 dark:bg-[#1e293b] z-10 shadow-[1px_0_0_0_#e2e8f0] dark:shadow-[1px_0_0_0_#334155]">
                      <div className="flex items-center gap-3">
                        <span className="text-gray-400 group-hover:text-primary transition-colors">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </span>
                        <span className="text-[11px] font-black text-gray-800 dark:text-gray-200 uppercase tracking-widest">
                          {mod.label}
                        </span>
                        <span className="text-[10px] text-gray-400 font-semibold bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-2 py-0.5 rounded-full">
                          {mod.permissions.length}
                        </span>
                      </div>
                    </td>
                    {initialRoles.map((role) => {
                      const moduleState = getModuleState(role.id, mod.key);
                      return (
                        <td
                          key={role.id}
                          className="text-center py-3 px-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex items-center justify-center">
                            <IndeterminateCheckbox
                              checked={moduleState === "all"}
                              indeterminate={moduleState === "some"}
                              onChange={(v) => handleModuleRoleToggle(role.id, mod.key, v)}
                              disabled={!canAdminEditRole(currentUser, role)}
                              title={!canAdminEditRole(currentUser, role) ? "Insufficient permissions to edit this role" : ""}
                            />
                          </div>
                        </td>
                      );
                    })}
                  </tr>

                  {/* Permission Rows */}
                  {isExpanded &&
                    filteredPerms.map((perm, idx) => (
                      <tr
                        key={perm.key}
                        className={`transition-colors hover:bg-primary/5 ${
                          idx % 2 === 0 ? "bg-white dark:bg-gray-900" : "bg-gray-50/80 dark:bg-gray-800/40"
                        }`}
                      >
                        <td className={`py-3 pl-14 pr-6 sticky left-0 z-10 shadow-[1px_0_0_0_#e2e8f0] dark:shadow-[1px_0_0_0_#334155] ${
                          idx % 2 === 0 ? "bg-white dark:bg-gray-900" : "bg-[#f9fafb] dark:bg-[#1e293b]"
                        }`}>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                              {perm.label}
                            </span>
                            
                            <PermissionInfoPopover
                              perm={perm}
                              allPermissions={allPermissions}
                              getPermissionDescription={getPermissionDescription}
                            />
                          </div>
                        </td>
                        {initialRoles.map((role) => {
                          const isChecked = permissionsState[role.id]?.[perm.key] ?? false;
                          const isOutOfScope = !isPermissionInScope(allPermissions.find(p => p.id === perm.key), role.scope, currentUser);
                          const isNotEditable = !canAdminEditRole(currentUser, role);
                          const isDisabled = isOutOfScope || isNotEditable;
                          
                          // A permission is "auto-enabled" (locked on) when another ACTIVE permission
                          // in this role declares it as a required dependency.
                          // e.g. worklists:view is locked when worklists:update is active and depends on it.
                          const isAutoEnabled = isChecked && allPermissions.some(otherPerm =>
                            permissionsState[role.id]?.[otherPerm.id] &&        // the other perm is active
                            otherPerm.depends_on?.includes(perm.key)             // and it depends on THIS perm
                          );

                          return (
                            <PermissionCell
                              key={role.id}
                              checked={isChecked}
                              onChange={(v) => handlePermissionChange(role.id, perm.key, v)}
                              autoEnabled={isAutoEnabled}
                              disabled={isDisabled}
                              title={isOutOfScope ? "Permission out of scope for this role" : isNotEditable ? "Insufficient permissions to edit this role" : ""}
                            />
                          );
                        })}
                      </tr>
                    ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>

        {filteredModules.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Search className="w-12 h-12 text-gray-300 dark:text-gray-700 mb-4" />
            <p className="text-gray-500 dark:text-gray-400 font-medium">No permissions found</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Try adjusting your search criteria</p>
          </div>
        )}
      </div>

    </div>
  );
}
